import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useOptimizedImageUpload } from '@/hooks/useOptimizedImageUpload';

interface ImageUploaderProps {
  currentImageUrl?: string;
  onImageUploaded: (url: string) => void;
  bucketPath?: string;
  size?: 'sm' | 'md' | 'lg' | 'banner' | 'default';
  children?: React.ReactNode;
}

export function ImageUploader({ 
  currentImageUrl, 
  onImageUploaded, 
  bucketPath = 'avatar',
  size = 'md',
  children 
}: ImageUploaderProps) {
  const { uploadImage, uploading } = useOptimizedImageUpload();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-20 w-20',
    lg: 'h-32 w-32',
    default: 'h-20 w-20',
    banner: 'h-32 w-full'
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleUpload = async (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setPreviewUrl(previewUrl);

    const result = await uploadImage(file, {
      bucketPath,
      generateThumbnail: size === 'banner',
      compressionOptions: {
        maxWidth: size === 'banner' ? 1200 : 600,
        maxHeight: size === 'banner' ? 600 : 600,
        quality: size === 'banner' ? 0.9 : 0.925
      }
    });

    if (result) {
      onImageUploaded(result.url);
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    } else {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    onImageUploaded('');
    setPreviewUrl(null);
  };

  const displayImageUrl = previewUrl || currentImageUrl;

  if (children) {
    return (
      <div 
        className="relative group cursor-pointer"
        onClick={triggerFileSelect}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {children}
        
        {/* Overlay hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
          {uploading ? (
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          ) : (
            <Camera className="h-6 w-6 text-white" />
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div 
        className={`relative group cursor-pointer ${size === 'banner' ? 'w-full' : sizeClasses[size]}`}
        onClick={triggerFileSelect}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {size === 'banner' ? (
          <div className="w-full h-32 border border-border rounded-xl bg-muted/20 flex items-center justify-center overflow-hidden">
            {displayImageUrl ? (
              <img 
                src={displayImageUrl} 
                alt="Image de couverture" 
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <>
                    <Camera className="h-8 w-8 mb-2" />
                    <span className="text-sm">Image de couverture</span>
                    <span className="text-xs">Recommandé : 16:9</span>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-32 border border-border rounded-xl bg-muted/20 flex items-center justify-center overflow-hidden">
            {displayImageUrl ? (
              <img 
                src={displayImageUrl} 
                alt="Photo de profil" 
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <Camera className="h-8 w-8" />
                )}
              </div>
            )}
          </div>
        )}

        {/* Overlay hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
          {uploading ? (
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          ) : (
            <Camera className="h-6 w-6 text-white" />
          )}
        </div>

        {/* Bouton supprimer */}
        {displayImageUrl && !uploading && (
          <Button
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              removeImage();
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>


      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}